/// <reference path='../../../typings/index.d.ts' />
import { AuthenticationDataUpdate, ModelDataUpdate } from '../../Events';
import DataStore from '../../stores/DataStore';
import { defined } from '../../utility/Control';
import { Cadastre } from '../../model/Cadastre';
import StringFormatter from '../../utility/StringFormatter';
import { HousingValue } from '../../model/HousingValue';
import Fetcher from '../../utility/Fetcher';
import Marker = L.Marker;
import LatLngExpression = L.LatLngExpression;
import Icon = L.Icon;
import Popup = L.Popup;
import { Token } from '../../realty/model/Token';
import FeatureGroup = L.FeatureGroup;
import Map = L.Map;
import ILayer = L.ILayer;
import Config from '../../utility/Config'

const AHV_MAP_HEADER_ID = 'ahv-map-display-header';
const MAP_CONTAINER_ID = 'map-container';

const ICON_WIDTH = 32;
const ICON_HEIGHT = 37;
const ICON_ANCHOR_WIDTH = 16;
const ICON_ANCHOR_HEIGHT = 34;
const POPUP_ANCHOR_WIDTH = 0;
const POPUP_ANCHOR_HEIGHT = -38;

const LOWER_CASE_ALPHABET = 'abcdefghijklmnopqrstuvwxyz';
const markerIcons: Icon[] = [];
for (let i = 0; i < LOWER_CASE_ALPHABET.length; ++i) {
  const icon = L.icon({
    iconUrl: 'assets/images/icons/letter_' + LOWER_CASE_ALPHABET.charAt(i) + '.png',
    iconSize: [ICON_WIDTH, ICON_HEIGHT],
    iconAnchor: [ICON_ANCHOR_WIDTH, ICON_ANCHOR_HEIGHT],
    popupAnchor: [POPUP_ANCHOR_WIDTH, POPUP_ANCHOR_HEIGHT]
  });
  markerIcons.push(icon);
}

let inFullscreen = false;

export function init(tag: any, L: any): void {
  tag.on('mount', () => {
    DataStore.addListener(ModelDataUpdate, onModelDataUpdate);
    DataStore.addListener(AuthenticationDataUpdate, onAuthenticationDataUpdate);

    if (defined(DataStore.getAuthenticationData())) {
        onAuthenticationDataUpdate();
    }

  });
  tag.on('unmount', () => {
    DataStore.removeListener(ModelDataUpdate, onModelDataUpdate);
    DataStore.removeListener(AuthenticationDataUpdate, onAuthenticationDataUpdate);
  });

  const markerGroup: FeatureGroup<ILayer> = L.featureGroup();

  function initializeAmbitaMap(authenticationData: Token): void {
    if (tag.map) {
      return;
    }

    tag.map = L.ambita.map(tag[MAP_CONTAINER_ID], 'Bearer ' + authenticationData.access_token, {
      restApi: Config.restBaseUrl,
      refreshToken: authenticationData.refresh_token,
      fullscreenControl: true
    });

    tag.map.on('enterFullscreen', () => {
      inFullscreen = true;
    });

    tag.map.on('exitFullscreen', () => {
      inFullscreen = false;
    });

    tag.map.addLayer(markerGroup);
  }

  function onAuthenticationDataUpdate(): void {
    const authenticationData = DataStore.getAuthenticationData();
    if (!tag.map) {
      initializeAmbitaMap(authenticationData);
    }
    tag.update();
  }

  function onModelDataUpdate(): void {
    const cadastre: Cadastre = DataStore.getCadastre();
    if (defined(tag.map, cadastre)) {
      tag.map._onResize();
      tag.map.invalidateSize();
      const key = cadastre.ident.municipalityNumber < 1000 ? '0' + cadastre.key : cadastre.key;
      tag.map.showCadastre(key, {
        clearLayer: true,
        fitToFeature: true,
        callback: (featureGroup: FeatureGroup<ILayer>, options: any) => {
          tag.map.showCadastreLayer(featureGroup, options, tag.map);
          updateMapMarkers(tag.map, markerGroup, DataStore.getSurroundingHousingValues());
        }
      });
    }
  }
}

function updateMapMarkers(map: Map, markerGroup: FeatureGroup<ILayer>, housingValues: HousingValue[]): void {
  markerGroup.clearLayers();
  if (housingValues) {
    for (let i = 0; i < housingValues.length && i < markerIcons.length; ++i) {
      const housingValue = housingValues[i];
      if (housingValue.cadastreKey && housingValue.position && housingValue.calculatedValue) {
        const address = housingValue.address
          ? housingValue.address
          : StringFormatter.formatCadastreKey(housingValue.cadastreKey);
        markerGroup.addLayer(
          new HousingValueMarker(
            housingValue.cadastreKey,
            address,
            [housingValue.position[1], housingValue.position[0]],
            housingValue.calculatedValue,
            markerIcons[i]
          )
        );
      }
    }
  }
  const bounds = markerGroup.getBounds();
  if (bounds.isValid()) {
    map.fitBounds(bounds);
  }
}

class HousingValueMarker extends Marker {
  private cadastreKey: string;
  private address: string;
  private popup: Popup;
  private popupContent: string;
  private fullscreenPopupContent: string;

  private static clearHeader(): void {
    document.getElementById(AHV_MAP_HEADER_ID).innerHTML = '&nbsp;';
  }

  public constructor(
    cadastreKey: string,
    address: string,
    latLng: LatLngExpression,
    calculatedValue: number,
    icon: Icon
  ) {
    super(latLng, {
      icon,
      opacity: 0.5,
      riseOnHover: true
    });
    this.cadastreKey = cadastreKey;
    this.address = address;
    this.popupContent = 'Estimert verdi: Kr. ' + StringFormatter.formatMonetaryAmount(calculatedValue.toString());
    this.fullscreenPopupContent = this.address + '<br>' + this.popupContent;
    this.popup = L.popup({
      closeButton: false
    });
    this.bindPopup(this.popup);
    this.clearAllEventListeners();
    this.addEventListeners();
  }

  private addEventListeners(): void {
    this.on('click', () => {
      Fetcher.fetchModelData(this.cadastreKey);
      this.closePopup();
      HousingValueMarker.clearHeader();
    });
    this.on('mouseover', () => {
      this.setOpacity(1);
      this.popup.setContent(inFullscreen ? this.fullscreenPopupContent : this.popupContent);
      this.openPopup();
      document.getElementById(AHV_MAP_HEADER_ID).innerHTML = this.address;
    });
    this.on('mouseout', () => {
      const opacity = 0.5;
      this.closePopup();
      this.setOpacity(opacity);
      HousingValueMarker.clearHeader();
    });
  }
}



/** WEBPACK FOOTER **
 ** ./src/components/mapDisplay/MapDisplayController.ts
 **/